<section class="space-y-5">

  <!-- Header -->
  <div>
    <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">Webhook Trigger</h1>
    <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Manually dispatch webhook events to external systems when automatic delivery fails.</p>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

    <!-- Form -->
    <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div class="px-5 py-3.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
        <h2 class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Configuration</h2>
      </div>
      <form [formGroup]="form" (ngSubmit)="trigger()" class="p-5 space-y-4">

        <!-- Webhook URL -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
            Webhook URL
            <span class="ml-1 text-[10px] font-normal text-slate-400 normal-case tracking-normal">optional — uses default if empty</span>
          </label>
          <input formControlName="url" type="url" placeholder="https://your-endpoint.com/webhook"
            class="w-full h-10 px-3 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-800 focus:border-primary-800 text-slate-900 dark:text-white placeholder-slate-400 transition-colors bg-white dark:bg-slate-800" />
        </div>

        <!-- Loan Reference -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
            Loan Reference <span class="text-rose-500">*</span>
          </label>
          <input formControlName="loanId" type="text" placeholder="e.g. a1b2c3d4-..."
            class="w-full h-10 px-3 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-800 focus:border-primary-800 text-slate-900 dark:text-white placeholder-slate-400 transition-colors bg-white dark:bg-slate-800"
            [class.border-rose-300]="form.get('loanId')?.invalid && form.get('loanId')?.touched" />
          @if (form.get('loanId')?.invalid && form.get('loanId')?.touched) {
          <p class="text-rose-500 text-[11px] mt-1">Loan Reference is required.</p>
          }
        </div>

        <!-- Event -->
        <div>
          <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
            Loan Event <span class="text-rose-500">*</span>
          </label>
          <div class="relative">
            <select formControlName="event"
              class="w-full h-10 px-3 pr-10 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-800 focus:border-primary-800 text-slate-900 dark:text-white bg-white dark:bg-slate-800 appearance-none cursor-pointer transition-colors">
              @for (evt of webhookEvents; track evt.value) {
              <option [value]="evt.value">{{ evt.label }}</option>
              }
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Submit -->
        <button type="submit" [disabled]="isLoading"
          class="w-full h-10 flex items-center justify-center gap-2 bg-primary-800 text-white text-xs font-semibold rounded-lg hover:bg-primary-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2">
          @if (isLoading) {
          <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          Sending...
          } @else {
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Trigger Webhook
          }
        </button>

      </form>
    </div>

    <!-- Payload Preview -->
    <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div class="px-5 py-3.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 flex items-center justify-between">
        <h2 class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Payload Preview</h2>
        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>POST
        </span>
      </div>
      <div class="p-5">
        <!-- Endpoint -->
        <div class="mb-4">
          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Endpoint</p>
          <div class="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg min-w-0">
            <svg class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <p class="text-[11px] font-mono text-slate-600 dark:text-slate-400 truncate">
              {{ form.get('url')?.value?.trim() || 'Default API endpoint' }}
            </p>
          </div>
        </div>

        <!-- Body -->
        <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Body</p>
        <div class="bg-slate-900 rounded-lg p-4">
          <pre class="text-xs text-emerald-400 font-mono leading-relaxed whitespace-pre-wrap">{{ payloadPreview }}</pre>
        </div>
      </div>
    </div>

  </div>
</section>

<!-- Confirm Trigger Dialog -->
@if (showConfirmDialog) {
<app-confirmation-dialog
  [title]="'Trigger Webhook'"
  [message]="'This will send a ' + selectedEventLabel + ' event for loan reference \'' + (form.value.loanId || '') + '\' to ' + (form.value.url?.trim() || 'the default API endpoint') + '. The receiving system may act on it immediately and this cannot be undone.'"
  [confirmText]="'Send Webhook'"
  [cancelText]="'Cancel'"
  [type]="'warning'"
  (onConfirm)="confirmTrigger()"
  (onCancel)="cancelTrigger()">
</app-confirmation-dialog>
}
